import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Clock, CheckCircle, Zap, RotateCcw, Play } from 'lucide-react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import { openCustomerPortal } from '../../utils/cn';
import Footer from '../../components/Footer';

const InstantEstimate = () => {
  const [resumeSessionId, setResumeSessionId] = useState(null);
  const [showResumeBanner, setShowResumeBanner] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const savedSessionId = localStorage.getItem('estimate_session_id');
    if (savedSessionId) {
      setResumeSessionId(savedSessionId);
      setShowResumeBanner(true);
    }
  }, []);

  const handleResume = () => {
    openCustomerPortal();
    setShowResumeBanner(false);
  };

  const handleStartOver = () => {
    localStorage.removeItem('estimate_session_id');
    setResumeSessionId(null);
    setShowResumeBanner(false);
    openCustomerPortal();
  };

  const handleGetEstimate = () => {
    openCustomerPortal();
  };

  const trustIndicators = [
    { icon: Clock, text: 'Results in 30 minutes', color: 'text-blue-600' },
    { icon: Shield, text: '100% Free & No Obligation', color: 'text-green-600' },
    { icon: CheckCircle, text: 'No Sales Calls', color: 'text-purple-600' },
    { icon: Zap, text: 'RoofMatch Technology', color: 'text-orange-600' }
  ];

  const roofImages = [
    {
      src: '/assets/images/ChatGPT_Image_Jan_9_2026_at_12_08_44_PM-1768592410818.png',
      alt: 'Modern home with OpenbooQ NEW ROOF sign displayed in front yard'
    },
    {
      src: '/assets/images/iStock-2204640023-1768493985057.jpg',
      alt: 'Woman using OpenbooQ platform on laptop for roofing estimate'
    },
    {
      src: '/assets/images/iStock-1589375166-1768494640042.jpg',
      alt: 'Happy family with parents and children playing in front of their home'
    }
  ];

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden pt-20 lg:pt-28">
        {/* Resume Banner */}
        {showResumeBanner && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 lg:top-28 left-0 right-0 z-50 px-4 py-3"
          >
            <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl shadow-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3 text-white">
                <RotateCcw className="w-6 h-6 flex-shrink-0" />
                <p className="font-semibold text-lg">Resume your quote?</p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleResume}
                  className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-6 py-2 rounded-xl shadow-lg flex items-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  Resume
                </Button>
                <Button
                  onClick={handleStartOver}
                  className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-semibold px-6 py-2 rounded-xl"
                >
                  Start over
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.05, 0.1, 0.05],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-0 right-0 w-96 h-96 bg-blue-400 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.05, 0.1, 0.05],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
            className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"
          />
        </div>

        <div className="relative z-10 py-12 lg:py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Side - Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center lg:text-left"
              >
                {/* Eyebrow */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                  className="mb-6"
                >
                  <span className="text-3xl md:text-4xl font-bold text-gray-900">
                    Roofing Reimagined
                  </span>
                </motion.div>

                {/* Headline */}
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
                >
                  Your Roof Price in 30 Minutes
                  <span className="block bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                    You Can Trust
                  </span>
                </motion.h1>

                {/* Subtext */}
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  className="text-lg md:text-xl text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0"
                >
                  Get accurate pricing for your roofing project in minutes. No hidden fees, no surprises.
                </motion.p>

                {/* CTA Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="mb-6"
                >
                  <Button
                    onClick={handleGetEstimate}
                    size="xl"
                    className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold px-12 py-6 text-lg rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                  >
                    Get Roof Price
                  </Button>
                </motion.div>

                {/* Powered by */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                  className="text-sm text-gray-500"
                >
                  Powered by OpenbooQ AI
                </motion.p>

                {/* Trust Indicators */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.6 }}
                  className="flex flex-wrap justify-center lg:justify-start gap-4 mt-8"
                >
                  {trustIndicators?.map((indicator, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.7 + index * 0.1 }}
                      whileHover={{ scale: 1.05 }}
                      className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md border border-gray-100"
                    >
                      <indicator.icon className={`w-5 h-5 ${indicator?.color}`} />
                      <span className="text-sm font-medium text-gray-700">
                        {indicator?.text}
                      </span>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>

              {/* Right Side - Image Collage */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="relative"
              >
                <div className="grid grid-cols-2 gap-4">
                  {roofImages?.map((image, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                      whileHover={{ scale: 1.05, rotate: index % 2 === 0 ? 2 : -2 }}
                      className={`relative overflow-hidden rounded-3xl shadow-xl ${
                        index === 0 ? 'col-span-2' : ''
                      }`}
                    >
                      <img
                        src={image?.src}
                        alt={image?.alt}
                        className={`w-full h-full ${
                          index === 0 ? 'object-contain bg-gray-100' : 'object-cover'
                        }`}
                        style={{
                          height: index === 0 ? '280px' : '200px'
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* SEO Body Copy Section */}
        <section className="py-16 px-4 bg-white border-t border-gray-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Get an Accurate Roof Replacement Price in Minutes</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-5">
              OpenbooQ's roof price tool is designed for homeowners who want real pricing without the hassle of scheduling multiple contractor visits. Using satellite-based roof measurement technology and live material pricing, our platform generates a detailed, itemized price for your specific home — including materials, labor, tear-off, and disposal — in under 30 minutes.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Unlike generic "ballpark" calculators that give you a vague price range, OpenbooQ's prices are based on your actual roof dimensions, your chosen shingle product, and current market pricing in your area. That means the number you see is the number you pay — no surprise add-ons, no inflated quotes, no negotiating.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Obligation</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Your roof price is completely free with zero commitment. Browse pricing, compare shingle options, and explore financing — all before speaking to anyone. There are no sales calls triggered by getting your roof price.
                </p>
              </div>
              <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Transparent Pricing</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Every line item in your price is explained. You'll see exactly what you're paying for materials, labor, and our 20% Service & Warranty fee — which covers quality assurance, warranty administration, and dedicated project support.
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Powered by RoofMatch™</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Our proprietary RoofMatch™ technology analyzes your roof's unique characteristics — pitch, complexity, valleys, and penetrations — to ensure your price accounts for every factor that affects the final cost of your roof replacement.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      {/* Wizard Modal */}
      {/* removed EstimateWizardModal - all estimates now open Demand IQ */}
      <Footer />
    </>
  );
};

export default InstantEstimate;